import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useCurrency } from '../utils/currency';
import { useAuth } from '../context/AuthContext';
import {
  CubeIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  TruckIcon,
  ArrowsRightLeftIcon,
  CurrencyRupeeIcon
} from '@heroicons/react/24/outline';

export default function InventoryPage() {
  const { user } = useAuth();
  const { formatCurrency } = useCurrency(user?.gym_id);
  const [activeTab, setActiveTab] = useState('items'); // 'items', 'suppliers', 'inventory'

  // Data states
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showAdjustModal, setShowAdjustModal] = useState(false);

  // Forms
  const [itemForm, setItemForm] = useState({
    item_name: '',
    category: 'Supplement',
    sku: '',
    opening_stock: 0,
    min_stock_level: 5,
    cost_price: 0,
    selling_price: 0,
    supplier_id: '',
    notes: ''
  });

  const [supplierForm, setSupplierForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    gstin: ''
  });

  const [adjustForm, setAdjustForm] = useState({
    item_id: '',
    supplier_id: '',
    type: 'purchase', // purchase, adjustment, sale, return
    direction: 'add', // add or reduce
    quantity: 1,
    unit_price: 0,
    reason: 'Restock purchase'
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError('');
    try {
      const [itemsRes, suppliersRes, movementsRes] = await Promise.all([
        api.get('/api/inventory/items').catch(() => ({ data: { items: [] } })),
        api.get('/api/inventory/suppliers').catch(() => ({ data: { suppliers: [] } })),
        api.get('/api/inventory/movements').catch(() => ({ data: { movements: [] } }))
      ]);

      setItems(itemsRes.data.items || []);
      setSuppliers(suppliersRes.data.suppliers || []);
      setMovements(movementsRes.data.movements || []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  // Item handlers
  const handleSaveItem = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/api/inventory/items/${editingItem.id}`, itemForm);
      } else {
        await api.post('/api/inventory/items', itemForm);
      }
      setShowAddItemModal(false);
      setEditingItem(null);
      resetItemForm();
      fetchAllData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save item');
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await api.delete(`/api/inventory/items/${id}`);
      fetchAllData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete item');
    }
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      item_name: item.item_name,
      category: item.category || 'Supplement',
      sku: item.sku || '',
      opening_stock: item.quantity || 0,
      min_stock_level: item.reorder_level || 5,
      cost_price: item.cost_price || 0,
      selling_price: item.selling_price || 0,
      supplier_id: item.supplier_id || '',
      notes: item.notes || ''
    });
    setShowAddItemModal(true);
  };

  const resetItemForm = () => {
    setItemForm({
      item_name: '',
      category: 'Supplement',
      sku: '',
      opening_stock: 0,
      min_stock_level: 5,
      cost_price: 0,
      selling_price: 0,
      supplier_id: '',
      notes: ''
    });
  };

  // Supplier handlers
  const handleSaveSupplier = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await api.put(`/api/inventory/suppliers/${editingSupplier.id}`, supplierForm);
      } else {
        await api.post('/api/inventory/suppliers', supplierForm);
      }
      setShowAddSupplierModal(false);
      setEditingSupplier(null);
      resetSupplierForm();
      fetchAllData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save supplier');
    }
  };

  const handleDeleteSupplier = async (id) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;
    try {
      await api.delete(`/api/inventory/suppliers/${id}`);
      fetchAllData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete supplier');
    }
  };

  const openEditSupplier = (s) => {
    setEditingSupplier(s);
    setSupplierForm({
      name: s.name,
      phone: s.phone || '',
      email: s.email || '',
      address: s.address || '',
      gstin: s.gstin || ''
    });
    setShowAddSupplierModal(true);
  };

  const resetSupplierForm = () => {
    setSupplierForm({ name: '', phone: '', email: '', address: '', gstin: '' });
  };

  // Movement / Adjust Inventory handlers
  const handleSaveAdjustment = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/inventory/movements', adjustForm);
      setShowAdjustModal(false);
      setAdjustForm({
        item_id: '',
        supplier_id: '',
        type: 'purchase',
        direction: 'add',
        quantity: 1,
        unit_price: 0,
        reason: 'Restock purchase'
      });
      fetchAllData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to record stock movement');
    }
  };

  // Metrics calculations
  const totalItemsCount = items.length;
  const inStockCount = items.filter(i => i.quantity > i.reorder_level).length;
  const lowStockCount = items.filter(i => i.quantity <= i.reorder_level).length;

  const totalSuppliersCount = suppliers.length;

  const currentStockValue = items.reduce((sum, i) => sum + (i.quantity * i.cost_price), 0);
  const now = new Date();
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const totalStockInThisMonth = movements
    .filter(m => m.type === 'purchase' && new Date(m.date) >= currentMonthStart)
    .reduce((sum, m) => sum + Math.abs(m.quantity), 0);

  // Filtered Lists
  const filteredItems = items.filter(i =>
    i.item_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (i.sku && i.sku.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (i.category && i.category.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const filteredSuppliers = suppliers.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.email && s.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (s.phone && s.phone.includes(searchQuery))
  );

  const filteredMovements = movements.filter(m =>
    m.item_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (m.supplier_name && m.supplier_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (m.reason && m.reason.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {/* Top Header & Title */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CubeIcon className="w-7 h-7 text-indigo-600" /> Inventory & Stock Management
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Track stock items, suppliers, and audited inventory movements.
          </p>
        </div>

        {/* Action Button based on active tab */}
        <div className="mt-4 sm:mt-0 flex gap-3">
          {activeTab === 'items' && (
            <button
              onClick={() => { setEditingItem(null); resetItemForm(); setShowAddItemModal(true); }}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <PlusIcon className="w-5 h-5 mr-2" /> Add Item
            </button>
          )}
          {activeTab === 'suppliers' && (
            <button
              onClick={() => { setEditingSupplier(null); resetSupplierForm(); setShowAddSupplierModal(true); }}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <PlusIcon className="w-5 h-5 mr-2" /> Add Supplier
            </button>
          )}
          {activeTab === 'inventory' && (
            <button
              onClick={() => {
                if (items.length === 0) {
                  alert('Please add at least one item before adjusting inventory.');
                  return;
                }
                setAdjustForm({
                  item_id: items[0]?.id || '',
                  supplier_id: items[0]?.supplier_id || '',
                  type: 'purchase',
                  direction: 'add',
                  quantity: 1,
                  unit_price: items[0]?.cost_price || 0,
                  reason: 'Restock purchase'
                });
                setShowAdjustModal(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <ArrowsRightLeftIcon className="w-5 h-5 mr-2" /> Adjust Inventory
            </button>
          )}
        </div>
      </div>

      {/* Tabs Bar */}
      <div className="bg-gray-100/70 p-1.5 rounded-2xl flex gap-2 border border-gray-200/80 w-fit">
        <button
          onClick={() => { setActiveTab('items'); setSearchQuery(''); }}
          className={`px-5 py-2.5 rounded-xl font-medium text-sm flex items-center gap-2.5 transition-all duration-200 ${
            activeTab === 'items'
              ? 'bg-white text-indigo-600 shadow-sm font-semibold border border-gray-200/60'
              : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
          }`}
        >
          <CubeIcon className="w-5 h-5 text-indigo-500" /> Items
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
            activeTab === 'items' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-200/80 text-gray-700'
          }`}>
            {totalItemsCount}
          </span>
        </button>

        <button
          onClick={() => { setActiveTab('suppliers'); setSearchQuery(''); }}
          className={`px-5 py-2.5 rounded-xl font-medium text-sm flex items-center gap-2.5 transition-all duration-200 ${
            activeTab === 'suppliers'
              ? 'bg-white text-indigo-600 shadow-sm font-semibold border border-gray-200/60'
              : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
          }`}
        >
          <TruckIcon className="w-5 h-5 text-indigo-500" /> Suppliers
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
            activeTab === 'suppliers' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-200/80 text-gray-700'
          }`}>
            {totalSuppliersCount}
          </span>
        </button>

        <button
          onClick={() => { setActiveTab('inventory'); setSearchQuery(''); }}
          className={`px-5 py-2.5 rounded-xl font-medium text-sm flex items-center gap-2.5 transition-all duration-200 ${
            activeTab === 'inventory'
              ? 'bg-white text-indigo-600 shadow-sm font-semibold border border-gray-200/60'
              : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
          }`}
        >
          <ArrowsRightLeftIcon className="w-5 h-5 text-indigo-500" /> Inventory Movements
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
            activeTab === 'inventory' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-200/80 text-gray-700'
          }`}>
            {movements.length}
          </span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`Search ${activeTab}...`}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
        />
      </div>

      {/* Tab 1: Items */}
      {activeTab === 'items' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Total Items</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{totalItemsCount}</p>
              </div>
              <div className="p-3 bg-indigo-50 rounded-full text-indigo-600">
                <CubeIcon className="w-6 h-6" />
              </div>
            </div>

            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">In Stock</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{inStockCount}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-full text-green-600">
                <CheckCircleIcon className="w-6 h-6" />
              </div>
            </div>

            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Low Stock</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{lowStockCount}</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-full text-amber-600">
                <ExclamationTriangleIcon className="w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-white shadow border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Qty</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Selling Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {loading ? (
                  <tr><td colSpan="7" className="text-center py-6 text-gray-500">Loading items...</td></tr>
                ) : filteredItems.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-6 text-gray-500">No inventory items found.</td></tr>
                ) : (
                  filteredItems.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {item.item_name}
                        {item.sku && <span className="block text-xs text-gray-400">SKU: {item.sku}</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{item.category}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">{item.quantity}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{formatCurrency(item.selling_price)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{item.supplier || '-'}</td>
                      <td className="px-6 py-4">
                        {item.is_low_stock ? (
                          <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                            Low Stock (≤{item.reorder_level})
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                            In Stock
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right space-x-3">
                        <button onClick={() => openEditItem(item)} className="text-indigo-600 hover:text-indigo-900">
                          <PencilIcon className="w-4 h-4 inline" />
                        </button>
                        <button onClick={() => handleDeleteItem(item.id)} className="text-red-600 hover:text-red-900">
                          <TrashIcon className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab 2: Suppliers */}
      {activeTab === 'suppliers' && (
        <div className="space-y-6">
          <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm max-w-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Total Suppliers</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalSuppliersCount}</p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-full text-indigo-600">
              <TruckIcon className="w-6 h-6" />
            </div>
          </div>

          <div className="bg-white shadow border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items Supplied</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Purchase Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {loading ? (
                  <tr><td colSpan="5" className="text-center py-6 text-gray-500">Loading suppliers...</td></tr>
                ) : filteredSuppliers.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-6 text-gray-500">No suppliers found.</td></tr>
                ) : (
                  filteredSuppliers.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {s.name}
                        {s.gstin && <span className="block text-xs text-gray-400">GSTIN: {s.gstin}</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div>{s.phone || '-'}</div>
                        <div className="text-xs text-gray-400">{s.email}</div>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">{s.items_supplied_count || 0}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{s.last_purchase_date ? new Date(s.last_purchase_date).toLocaleDateString() : '-'}</td>
                      <td className="px-6 py-4 text-right space-x-3">
                        <button onClick={() => openEditSupplier(s)} className="text-indigo-600 hover:text-indigo-900">
                          <PencilIcon className="w-4 h-4 inline" />
                        </button>
                        <button onClick={() => handleDeleteSupplier(s.id)} className="text-red-600 hover:text-red-900">
                          <TrashIcon className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab 3: Inventory Movements */}
      {activeTab === 'inventory' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Current Stock Value</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(currentStockValue)}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-full text-green-600">
                <CurrencyRupeeIcon className="w-6 h-6" />
              </div>
            </div>

            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Total Stock-In This Month</p>
                <p className="text-2xl font-bold text-indigo-600 mt-1">{totalStockInThisMonth} units</p>
              </div>
              <div className="p-3 bg-indigo-50 rounded-full text-indigo-600">
                <ArrowsRightLeftIcon className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-white shadow border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type / Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {loading ? (
                  <tr><td colSpan="7" className="text-center py-6 text-gray-500">Loading movements...</td></tr>
                ) : filteredMovements.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-6 text-gray-500">No stock movements recorded yet.</td></tr>
                ) : (
                  filteredMovements.map((m) => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-500">{new Date(m.date).toLocaleDateString()}</td>
                      <td className="px-6 py-4 font-medium text-gray-900">{m.item_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold mr-2 ${
                          m.type === 'purchase' ? 'bg-green-100 text-green-800' :
                          m.type === 'sale' ? 'bg-blue-100 text-blue-800' :
                          m.type === 'return' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {m.type.toUpperCase()}
                        </span>
                        {m.reason}
                      </td>
                      <td className={`px-6 py-4 text-sm font-bold ${m.quantity >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {m.quantity >= 0 ? `+${m.quantity}` : m.quantity}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{formatCurrency(m.unit_price)}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{formatCurrency(m.total_price)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{m.supplier_name || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal: Add/Edit Item */}
      {showAddItemModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4">
            <h2 className="text-lg font-bold text-gray-900">{editingItem ? 'Edit Item' : 'Add New Item'}</h2>
            <form onSubmit={handleSaveItem} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700">Item Name *</label>
                <input
                  type="text"
                  required
                  value={itemForm.item_name}
                  onChange={(e) => setItemForm({ ...itemForm, item_name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700">Category</label>
                  <select
                    value={itemForm.category}
                    onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="Supplement">Supplement</option>
                    <option value="Equipment">Equipment</option>
                    <option value="Apparel">Apparel</option>
                    <option value="Beverage">Beverage</option>
                    <option value="Accessory">Accessory</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">SKU</label>
                  <input
                    type="text"
                    value={itemForm.sku}
                    onChange={(e) => setItemForm({ ...itemForm, sku: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700">Cost Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={itemForm.cost_price}
                    onChange={(e) => setItemForm({ ...itemForm, cost_price: parseFloat(e.target.value) || 0 })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Selling Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={itemForm.selling_price}
                    onChange={(e) => setItemForm({ ...itemForm, selling_price: parseFloat(e.target.value) || 0 })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700">{editingItem ? 'Current Stock' : 'Opening Stock'}</label>
                  <input
                    type="number"
                    value={itemForm.opening_stock}
                    disabled={Boolean(editingItem)}
                    onChange={(e) => setItemForm({ ...itemForm, opening_stock: parseInt(e.target.value) || 0 })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Min Stock Level</label>
                  <input
                    type="number"
                    value={itemForm.min_stock_level}
                    onChange={(e) => setItemForm({ ...itemForm, min_stock_level: parseInt(e.target.value) || 0 })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700">Supplier</label>
                <select
                  value={itemForm.supplier_id}
                  onChange={(e) => setItemForm({ ...itemForm, supplier_id: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                >
                  <option value="">-- Select Supplier --</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowAddItemModal(false)}
                  className="px-4 py-2 text-sm border rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Save Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Add/Edit Supplier */}
      {showAddSupplierModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4">
            <h2 className="text-lg font-bold text-gray-900">{editingSupplier ? 'Edit Supplier' : 'Add Supplier'}</h2>
            <form onSubmit={handleSaveSupplier} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700">Supplier Name *</label>
                <input
                  type="text"
                  required
                  value={supplierForm.name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700">Phone</label>
                  <input
                    type="text"
                    value={supplierForm.phone}
                    onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">GSTIN (Optional)</label>
                  <input
                    type="text"
                    value={supplierForm.gstin}
                    onChange={(e) => setSupplierForm({ ...supplierForm, gstin: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={supplierForm.email}
                  onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">Address</label>
                <textarea
                  rows="2"
                  value={supplierForm.address}
                  onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowAddSupplierModal(false)}
                  className="px-4 py-2 text-sm border rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Save Supplier
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Adjust Inventory */}
      {showAdjustModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Adjust Inventory Stock</h2>
            <form onSubmit={handleSaveAdjustment} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700">Select Item *</label>
                <select
                  required
                  value={adjustForm.item_id}
                  onChange={(e) => {
                    const selItem = items.find(i => i.id === parseInt(e.target.value));
                    setAdjustForm({
                      ...adjustForm,
                      item_id: e.target.value,
                      supplier_id: selItem?.supplier_id || '',
                      unit_price: adjustForm.type === 'purchase' ? (selItem?.cost_price || 0) : (selItem?.selling_price || 0)
                    });
                  }}
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                >
                  {items.map(i => (
                    <option key={i.id} value={i.id}>{i.item_name} (Current: {i.quantity})</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700">Movement Type</label>
                  <select
                    value={adjustForm.type}
                    onChange={(e) => setAdjustForm({ ...adjustForm, type: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="purchase">Purchase (Stock In)</option>
                    <option value="adjustment">Adjustment (Count/Correction)</option>
                    <option value="sale">Sale (Customer Out)</option>
                    <option value="return">Return</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Action</label>
                  <select
                    value={adjustForm.direction}
                    onChange={(e) => setAdjustForm({ ...adjustForm, direction: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="add">Increase (+) Stock</option>
                    <option value="reduce">Reduce (-) Stock</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700">Quantity *</label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={adjustForm.quantity}
                    onChange={(e) => setAdjustForm({ ...adjustForm, quantity: parseInt(e.target.value) || 1 })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Unit Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={adjustForm.unit_price}
                    onChange={(e) => setAdjustForm({ ...adjustForm, unit_price: parseFloat(e.target.value) || 0 })}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700">Supplier (Optional)</label>
                <select
                  value={adjustForm.supplier_id}
                  onChange={(e) => setAdjustForm({ ...adjustForm, supplier_id: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                >
                  <option value="">-- Select Supplier --</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700">Reason / Notes *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Stock Restock / Damaged Batch / Return"
                  value={adjustForm.reason}
                  onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowAdjustModal(false)}
                  className="px-4 py-2 text-sm border rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Record Movement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
